import { Typography, makeStyles } from "@material-ui/core"
import { FC, useMemo } from "react"
import {
  VictoryAxis,
  VictoryChart,
  VictoryContainer,
  VictoryPie,
} from "victory"
import useCasemix from "../../hooks/useCasemix"
import { colors } from "../../utils/theme"

const LegendRow: FC<{
  label: string
  data: number
  backgroundColor: string
}> = ({ label, data, backgroundColor }) => {
  const classes = useStyles()
  return (
    <div className={classes.legendRow}>
      <div
        className={classes.legendCircle}
        style={{
          backgroundColor,
        }}
      />
      <Typography variant="body2">{`${data}% ${label}`}</Typography>
    </div>
  )
}

/**
 * Renders a pie chart displaying casemix data.
 */
export const CaseMixPieChart: FC = () => {
  const classes = useStyles()
  const { casemixForOrg, categoryDisplayValues } = useCasemix()

  const { data, displayColors, totalCount, legendData } = useMemo(() => {
    const data: number[] = []
    const displayColors: string[] = []
    let totalCount = 0
    const legendData: { label: string; count: number; color: string }[] = []
    categoryDisplayValues.forEach((value, key) => {
      const categorySize = casemixForOrg.get(key) ?? 0
      data.push(categorySize)
      totalCount += categorySize
      displayColors.push(value.color)
    })
    categoryDisplayValues.forEach((value, key) => {
      legendData.push({
        label: value.category,
        count: Math.round((data[key] / totalCount) * 100),
        color: value.color,
      })
    })
    // handle case where there is no data
    if (totalCount === 0) {
      return {
        data: [1],
        displayColors: [colors.neutral.clay],
        totalCount: 1,
        legendData: [
          { label: "No data", count: 100, color: colors.neutral.clay },
        ],
      }
    }
    return { data, displayColors, totalCount, legendData }
  }, [casemixForOrg, categoryDisplayValues])

  Array.from(casemixForOrg.keys()).map((key, index) => ({
    key,
    count: (data[index] / totalCount) * 100,
    color: displayColors[index],
  }))
  return (
    <div className={classes.casemix}>
      <VictoryChart
        padding={{ top: 10, bottom: 110, left: 0, right: 0 }}
        containerComponent={<VictoryContainer style={{ width: 200 }} />}
      >
        <VictoryPie
          data={data}
          innerRadius={64}
          colorScale={displayColors}
          labelComponent={<></>}
        />
        <VictoryAxis
          orientation="left"
          style={{
            axis: { stroke: "transparent" },
            ticks: { stroke: "transparent" },
            tickLabels: { fill: "transparent" },
          }}
        />
        <VictoryAxis
          orientation="bottom"
          style={{
            axis: { stroke: "transparent" },
            ticks: { stroke: "transparent" },
            tickLabels: { fill: "transparent" },
          }}
        />
      </VictoryChart>
      <div className={classes.legend}>
        <Typography variant="h4" className={classes.legendTitle}>
          Casemix
        </Typography>
        {legendData.map((legend) => (
          <LegendRow
            key={legend.label}
            label={legend.label}
            backgroundColor={legend.color}
            data={legend.count}
          />
        ))}
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  casemix: {
    display: "flex",
    flexDirection: "row",
    alignItems: "start",
    height: 169,
  },
  legend: {
    position: "relative",
    left: -20,
    marginTop: 20,
    minWidth: 160,
  },
  legendTitle: {
    marginBottom: theme.spacing(1),
  },
  legendRow: {
    display: "flex",
    alignItems: "center",
  },
  legendCircle: {
    height: 10,
    width: 10,
    borderRadius: "50%",
    marginRight: theme.spacing(1),
  },
}))
